import { Dispatch, SetStateAction } from 'react';

import {
  AlertIcon,
  AlienIcon,
  Button,
  CopyIcon,
  H3,
  InfoIcon,
  Label,
  Paragraph,
  Tooltip,
  TTranslator,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column, Row } from 'react-table';

import { TLocalesKeys } from 'localization';
import { IApplicableParamsParsed } from 'pages/PromoCodeManager/interfaces';
import { TPromoCampaignData, TPromoCodeData } from 'schemas';
import notification from 'services/notificationService';
import { EPromoValueType } from 'types';
import { copyToClipboard } from 'utils';

import s from '../../PromoCodes.module.scss';

export const useColumnsPromo = (
  t: TTranslator<TLocalesKeys>,
  currency: { value: string; sign: string },
  setClientUUID: Dispatch<SetStateAction<string | null>>
): Array<Column<TPromoCodeData>> => [
  {
    Header: t('promoCode'),
    accessor: 'code',
    Cell: ({ value }) => (
      <div className={s.name}>
        <span>{value}</span>
        <Button
          variant="ghost"
          className={s.control}
          onClick={() =>
            copyToClipboard(value)
              .then(() => {
                notification.info({
                  t,
                  type: 'success',
                  title: t('copy'),
                  dismiss: 5,
                });
              })
              .catch(() => {
                notification.info({
                  t,
                  type: 'error',
                  title: t('header.shell.copied.error'),
                  dismiss: 5,
                });
              })
          }
        >
          <CopyIcon />
        </Button>
      </div>
    ),
    width: 250,
    disableResizing: true,
  },
  {
    Header: t('amount'),
    accessor: 'amount',
    Cell: ({ value }) => <span>{value}</span>,
  },
  {
    Header: t('used'),
    accessor: 'counters',
    id: 'counters-used',
    Cell: ({ value, row }) => {
      if (value.used === row?.original?.amount) {
        return (
          <div className={s.used_label}>
            <AlertIcon color="error" size="s" />
            <span>{value.used}</span>
          </div>
        );
      }

      return <span>{value.used}</span>;
    },
  },
  {
    Header: t('promoCode.type'),
    id: 'value-type',
    accessor: ({ value }) => value.type,
    Cell: ({ value }: { value: EPromoValueType }) => {
      if (value === EPromoValueType.DISCOUNT)
        return (
          <Label
            color="orange"
            text={t('discount')}
            size="s"
            iconName="PercentIcon"
          />
        );

      return (
        <Label
          color="violet"
          text={t('renewal')}
          size="s"
          iconName="WalletIcon"
        />
      );
    },
    width: 170,
    disableResizing: true,
  },
  {
    Header: t('client'),
    id: 'client-scope',
    accessor: ({ client_scope }) => client_scope ?? 'all',
    Cell: ({ value }: { value: TPromoCodeData['client_scope'] | string }) => {
      if (value === 'all') {
        return <span>{t('all')}</span>;
      }
      if (typeof value !== 'string' && value[0].__typename === 'ScopedClient')
        return (
          <div className={s.client}>
            {value[0].avatar_url ? (
              <img src={value[0].avatar_url} alt={value[0].phone} />
            ) : (
              <div className={s.client__avatar}>
                <AlienIcon color="secondary" />
              </div>
            )}
            <span
              className={s.client__phone}
              onClick={() => setClientUUID(value[0].id)}
            >
              {value[0].phone}
            </span>
          </div>
        );

      if (typeof value !== 'string') {
        const clientGroups = value.map(({ title }) => title).join(', ');

        return <span>{clientGroups}</span>;
      }
    },
  },
  {
    Header: t('value'),
    id: 'promo-value',
    accessor: ({ value }) => {
      if (value.type === EPromoValueType.DISCOUNT) return `${value.value} %`;
      else if (value.type === EPromoValueType.DEPOSIT)
        return `${value.value} ${currency.sign}`;
      else
        return (
          <div className={s.bonus_value}>
            {value.value} <span className={s.icon}>⊙</span>
          </div>
        );
    },
    width: 125,
    disableResizing: true,
  },
  {
    Header: t('promoCode.applicable'),
    accessor: 'applicable_params',
    Cell: ({ value }) => {
      const data: IApplicableParamsParsed = JSON.parse(value);

      const { goods, services, tariffs } = data;

      const isAllSelected = goods?.all && services?.all && tariffs?.all;

      if (isAllSelected) return t('all');

      const selectedCategories = [];

      if (goods?.all || (goods?.items && goods?.items.length > 0)) {
        selectedCategories.push('goods');
      }
      if (services?.all || (services?.items && services?.items.length > 0)) {
        selectedCategories.push('services');
      }
      if (tariffs?.all || (tariffs?.items && tariffs?.items.length > 0)) {
        selectedCategories.push('tariffs');
      }

      const title = selectedCategories
        .map((key) => t(key as 'goods' | 'services' | 'tariffs'))
        .join(', ');

      const tooltipContent: Array<{
        type: 'goods' | 'services' | 'tariffs';
        text: string;
      }> = [];

      if (goods?.all || (goods?.items && goods?.items.length > 0)) {
        tooltipContent.push({
          type: 'goods',
          text: goods?.all
            ? t('all')
            : goods?.items?.map(({ title }) => title).join(', ') || '',
        });
      }

      if (services?.all || (services?.items && services?.items.length > 0)) {
        tooltipContent.push({
          type: 'services',
          text: services?.all
            ? t('all')
            : services?.items?.map(({ title }) => title).join(', ') || '',
        });
      }

      if (tariffs?.all || (tariffs?.items && tariffs?.items.length > 0)) {
        tooltipContent.push({
          type: 'tariffs',
          text: tariffs?.all
            ? t('all')
            : tariffs?.items?.map(({ title }) => title).join(', ') || '',
        });
      }

      return (
        <div className={s.applicable}>
          <Paragraph>{title}</Paragraph>
          <Tooltip
            contentClassName={s.tooltipContent}
            content={
              <div className={s.tooltip}>
                <H3 className={s.tooltip__title}>{t('promo.position')}</H3>
                <div className={s.tooltip__items}>
                  {tooltipContent.map(({ type, text }) => (
                    <div className={s.item} key={type}>
                      <Paragraph
                        color="secondary"
                        className={s.item__title}
                        size="s"
                      >
                        {t(type)}
                      </Paragraph>
                      <Paragraph className={s.item__text} size="s">
                        {text}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <InfoIcon color="link-info" size="s" />
          </Tooltip>
        </div>
      );
    },
    width: 235,
    disableResizing: true,
  },
  {
    Header: t('period.action'),
    id: 'active-period',
    accessor: ({ active_from, active_to }): null | string => {
      if (!active_from) return null;

      const nowYear = dayjs().year();
      const start = dayjs(active_from);
      const end = active_to ? dayjs(active_to) : null;

      const startYear = start.year();
      const endYear = end?.year();

      const format = (date: dayjs.Dayjs, hideYear: boolean): string =>
        hideYear ? date.format('DD MMM') : date.format('DD MMM YYYY');

      if (!end) {
        const hideYear = startYear === nowYear;
        return format(start, hideYear);
      }

      if (startYear === nowYear && endYear === nowYear) {
        return `${format(start, true)} — ${format(end, true)}`;
      }

      return `${format(start, false)} — ${format(end, false)}`;
    },
    width: 235,
    disableResizing: true,
  },
  {
    Header: t('status'),
    accessor: 'active',
    Cell: ({ value, row }: { value: boolean; row: Row<TPromoCodeData> }) => {
      return (
        <div className={s.active}>
          {row?.original?.is_tracked ? (
            <Label iconName="TelegramBellIcon" color="blue" size="s" />
          ) : (
            <Label
              iconName="TelegramBellOffIcon"
              color="gray-primary"
              size="s"
            />
          )}
          {value ? (
            <Label
              size="s"
              color="green"
              text={t('active')}
              className={s.active__available}
            />
          ) : (
            <Label
              size="s"
              color="gray-primary"
              text={t('not_active')}
              className={s.active__notAvailable}
            />
          )}
        </div>
      );
    },
    width: 200,
    disableResizing: true,
  },
];

// promo campaign
export const useColumnsCampaign = (
  t: TTranslator<TLocalesKeys>,
  setClientUUID: Dispatch<SetStateAction<string | null>>
): Array<Column<TPromoCampaignData>> => [
  {
    Header: t('title'),
    accessor: 'title',
    Cell: ({ value }) => (
      <div className={s.name}>
        <span>{value}</span>
      </div>
    ),
  },
  {
    Header: t('amount'),
    accessor: 'counters',
    Cell: ({ value }) => <span>{value.total}</span>,
  },
  {
    Header: t('used'),
    accessor: 'counters',
    id: 'counters-used',
    Cell: ({ value }) => {
      if (value.used === value.total) {
        return (
          <div className={s.used_label}>
            <AlertIcon color="error" size="s" />
            <span>{value.used}</span>
          </div>
        );
      }

      return <span>{value.used}</span>;
    },
  },
  {
    Header: t('promoCode.type'),
    id: 'value-type',
    accessor: ({ value }) => value.type,
    Cell: ({ value }: { value: EPromoValueType }) => {
      if (value === EPromoValueType.DISCOUNT)
        return (
          <Label
            color="orange"
            text={t('discount')}
            size="s"
            iconName="PercentIcon"
          />
        );

      return (
        <Label
          color="violet"
          text={t('renewal')}
          size="s"
          iconName="WalletIcon"
        />
      );
    },
  },
  {
    Header: t('client'),
    accessor: 'client_scope',
    Cell: ({ value }: { value: TPromoCodeData['client_scope'] }) => {
      if (value[0].__typename === 'ScopedClient')
        return (
          <div className={s.client}>
            {value[0].avatar_url ? (
              <img src={value[0].avatar_url} alt={value[0].phone} />
            ) : (
              <div className={s.client__avatar}>
                <AlienIcon color="secondary" />
              </div>
            )}
            <span
              className={s.client__phone}
              onClick={() => setClientUUID(value[0].id)}
            >
              {value[0].phone}
            </span>
          </div>
        );

      const clientGroups = value.map(({ title }) => title).join(', ');

      return <span>{clientGroups}</span>;
    },
  },
  {
    Header: t('value'),
    id: 'promo-value',
    accessor: ({ value }) => {
      if (value.type === EPromoValueType.DISCOUNT) return `${value.value} %`;
      else if (value.type === EPromoValueType.DEPOSIT)
        return `${value.value} ${value.currency?.letter}`;
      else
        return (
          <div className={s.bonus_value}>
            {value.value} <span className={s.icon}>⊙</span>
          </div>
        );
    },
  },
  {
    Header: t('promoCode.applicable'),
    accessor: 'applicable_params',
    Cell: ({ value }) => {
      const data: IApplicableParamsParsed = JSON.parse(value);

      const { goods, services, tariffs } = data;

      const isAllSelected = goods?.all && services?.all && tariffs?.all;

      if (isAllSelected) return t('all');

      const selectedCategories = [];

      if (goods?.all || (goods?.items && goods?.items.length > 0)) {
        selectedCategories.push('goods');
      }
      if (services?.all || (services?.items && services?.items.length > 0)) {
        selectedCategories.push('services');
      }
      if (tariffs?.all || (tariffs?.items && tariffs?.items.length > 0)) {
        selectedCategories.push('tariffs');
      }

      const title = selectedCategories
        .map((key) => t(key as 'goods' | 'services' | 'tariffs'))
        .join(', ');

      const tooltipContent: Array<{
        type: 'goods' | 'services' | 'tariffs';
        text: string;
      }> = [];

      if (goods?.all || (goods?.items && goods?.items.length > 0)) {
        tooltipContent.push({
          type: 'goods',
          text: goods?.all
            ? t('all')
            : goods?.items?.map(({ title }) => title).join(', ') || '',
        });
      }

      if (services?.all || (services?.items && services?.items.length > 0)) {
        tooltipContent.push({
          type: 'services',
          text: services?.all
            ? t('all')
            : services?.items?.map(({ title }) => title).join(', ') || '',
        });
      }

      if (tariffs?.all || (tariffs?.items && tariffs?.items.length > 0)) {
        tooltipContent.push({
          type: 'tariffs',
          text: tariffs?.all
            ? t('all')
            : tariffs?.items?.map(({ title }) => title).join(', ') || '',
        });
      }

      return (
        <div className={s.applicable}>
          <Paragraph>{title}</Paragraph>
          <Tooltip
            contentClassName={s.tooltipContent}
            content={
              <div className={s.tooltip}>
                <H3 className={s.tooltip__title}>{t('promo.position')}</H3>
                <div className={s.tooltip__items}>
                  {tooltipContent.map(({ type, text }) => (
                    <div className={s.item} key={type}>
                      <Paragraph
                        color="secondary"
                        className={s.item__title}
                        size="s"
                      >
                        {t(type)}
                      </Paragraph>
                      <Paragraph className={s.item__text} size="s">
                        {text}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <InfoIcon color="link-info" size="s" />
          </Tooltip>
        </div>
      );
    },
    width: 235,
    disableResizing: true,
  },
  {
    Header: t('period.action'),
    id: 'active-period',
    accessor: ({ active_from, active_to }): null | string => {
      if (!active_from) return null;

      const nowYear = dayjs().year();
      const start = dayjs(active_from);
      const end = active_to ? dayjs(active_to) : null;

      const startYear = start.year();
      const endYear = end?.year();

      const format = (date: dayjs.Dayjs, hideYear: boolean): string =>
        hideYear ? date.format('DD MMM') : date.format('DD MMM YYYY');

      if (!end) {
        const hideYear = startYear === nowYear;
        return format(start, hideYear);
      }

      if (startYear === nowYear && endYear === nowYear) {
        return `${format(start, true)} — ${format(end, true)}`;
      }

      return `${format(start, false)} — ${format(end, false)}`;
    },
    width: 265,
    disableResizing: true,
  },
  {
    Header: t('status'),
    accessor: 'active',
    Cell: ({ value, row }: { value: boolean; row: Row<TPromoCodeData> }) => {
      return (
        <div className={s.active}>
          {row?.original?.is_tracked ? (
            <Label iconName="TelegramBellIcon" color="blue" size="s" />
          ) : (
            <Label
              iconName="TelegramBellOffIcon"
              color="gray-primary"
              size="s"
            />
          )}
          {value ? (
            <Label
              size="s"
              color="green"
              text={t('active')}
              className={s.active__available}
            />
          ) : (
            <Label
              size="s"
              color="gray-primary"
              text={t('not_active')}
              className={s.active__notAvailable}
            />
          )}
        </div>
      );
    },
    width: 190,
    disableResizing: true,
  },
];
